import {
  Component,
  HostBinding,
  ViewChild,
  EventEmitter,
  ElementRef,
  AfterViewInit,
  Output,
  OnDestroy,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { ChatbotService } from 'src/services/chatbot-services/chatbot.service';
import { environment } from 'src/environments/environment';
import { LoggerService } from 'src/services/logger-services/logger.service';
import Shepherd from 'shepherd.js';

/**
 * WelcomeTourComponent manages an interactive onboarding experience,
 * guiding users through key UI elements of the chatbot application.
 * uses shepherd.js npm package for modern tooltip-like tour card.
 *
 * Features:
 * - Multi-step instructional tour.
 * - Dynamically positioned tooltip-like tour card.
 * - Automatically repositions on window resize or scroll.
 * - Emits step-based highlight events to the parent component.
 */
@Component({
  selector: 'app-welcome-tour',
  templateUrl: './welcome-tour.component.html',
  styleUrls: ['./welcome-tour.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WelcomeTourComponent implements OnInit,AfterViewInit, OnDestroy {
  
  /**
   * Emits the current step and flags indicating which element to highlight.
   */
  @Output() highlight = new EventEmitter<{
    step: number,
    highlightSend: boolean,
    highlightMic: boolean,
    highlightOptions: boolean,
    highlightPromptLibrary: boolean
    highlightChatHistoryBtn: boolean,
    highlightNewChatBtn: boolean,
    highlightHelpIcon: boolean,
    highlightPromptSection: boolean
  }>();

  @Input() userFName: string="";

  /**
   * TenantID of the current user
  */
  @Input() tenantId: any;

  /**
   * DomainID of the current user
  */
  @Input() domainId: any;

  /**
   * Input for initial Intro Wizard Enable boolean
  */
  @Input() isIntroWizardEnabled: boolean = false;

  /** Input Intro Wizard Status String value */
  @Input() introWizardStatus: string = "incomplete";
  
  /** Input Tour Enable Boolean value */
  @Input() isTourEnabled: boolean = false;

  /** Input Tour Status String value */
  @Input() tourStatus: any;

  /**
   * Emits when the tour is completed or skipped.
   */
  @Output() close = new EventEmitter<void>();

  /**
   * Emits when the tour visibility.
   */
  @Output() emitTourVisible = new EventEmitter<any>();

  /**
   * Reference to the DOM element of the tour card.
   */
  @ViewChild('tourCard') tourCard!: ElementRef;

  /**
   * Current step of the onboarding tour (1-indexed).
   */
  step: number = 1;

  /**
   * Flag controlling the visibility of the tour.
   */
  isTourVisible = false;

  /**
   * Flag checking if the tour card is positioned
   */
  isPositioned = false;

  /**
   * Name updation variable for welcome card
   */
  updatedWelcome: string = '';

  /**
   * Shepherd.js tour instance
   */
  private tour: any | null = null;

  /**
   * Flag to prevent duplicate API calls
   */
  private tourClosed: boolean = false;

  /** Taking all the necessary services in the constructor */
  constructor(
    private readonly chatbotService: ChatbotService,
    private readonly languageService: LanguageChangeService,
    private readonly translate: TranslateService,
    private readonly loggerService: LoggerService
  ) {}

  

  /**
   * Tour step data: includes title, content, and element selectors for Shepherd.js
   */
  steps = [
    {
      title: 'WELCOME_CARD_TITLE',
      content: this.updatedWelcome,
      element: null, // Welcome step has no specific element
      position: 'center'
    },
    {
      title: 'WELCOME_TOUR_STEP_1_TITLE',
      content: 'WELCOME_TOUR_STEP_1_CONTENT',
      element: '.prompt-section',
      position: 'top'
    },
    {
      title: 'WELCOME_TOUR_STEP_2_TITLE',
      content: 'WELCOME_TOUR_STEP_2_CONTENT',
      element: '.btn-chat-send',
      position: 'left'
    },
    {
      title: 'WELCOME_TOUR_STEP_8_TITLE',
      content: 'WELCOME_TOUR_STEP_8_CONTENT',
      element: '.mic-button',
      position: 'left'
    },
    {
      title: 'WELCOME_TOUR_STEP_3_TITLE',
      content: 'WELCOME_TOUR_STEP_3_CONTENT',
      element: '.dropdown-selected',
      position: 'bottom'
    },
    {
      title: 'WELCOME_TOUR_STEP_4_TITLE',
      content: 'WELCOME_TOUR_STEP_4_CONTENT',
      element: '.prompt-library',
      position: 'bottom'
    },
    {
      title: 'WELCOME_TOUR_STEP_5_TITLE',
      content: 'WELCOME_TOUR_STEP_5_CONTENT',
      element: '.chat-menu-item',
      position: 'right'
    },
    {
      title: 'WELCOME_TOUR_STEP_6_TITLE',
      content: 'WELCOME_TOUR_STEP_6_CONTENT',
      element: '#new_topic_button',
      position: 'right'
    },
    {
      title: 'WELCOME_TOUR_STEP_7_TITLE',
      content: 'WELCOME_TOUR_STEP_7_CONTENT',
      element: '.help-fab',
      position: 'left'
    },

  ];

  /** Error template for WELCOME TOUR */
  private readonly welcomeTourError = (error: any) => 
    `[WELCOME TOUR] - ${JSON.stringify(error)}`;

  /**
   * Dynamically binds a CSS class (e.g., step-2) to the host element based on current step.
   */
  @HostBinding('class') get hostClass() {
    return `step-${this.step}`;
  }

  /**
   * Angular lifecycle hook - initialization.
   * Updates UserName immediately after initialization and initializes Shepherd tour
   */
  ngOnInit(): void {
    try {
      this.isTourVisible = true;
      const translatedWelcome = this.translate.instant('WELCOME_CARD_CONTENT')
      this.updatedWelcome = translatedWelcome.replace("{{userFName}}", this.userFName);
      
      // Update the welcome step content
      this.steps[0].content = this.updatedWelcome;
      
      // Initialize Shepherd tour
      this.initializeTour();
    } catch (error) {
      this.loggerService.logError(this.welcomeTourError(error),"ngOnInit");
      // Fallback to ensure component doesn't break
      this.isTourVisible = true;
      this.updatedWelcome = `Welcome ${this.userFName}!`;
      this.steps[0].content = this.updatedWelcome;
      this.initializeTour();
    }
  }
  

  /**
   * Angular lifecycle hook - called after view initialization.
   * Starts the Shepherd tour after DOM elements are available.
   */
  ngAfterViewInit() {
    try {
      // Start the tour after a short delay to ensure all elements are rendered
      setTimeout(() => {
        this.startTour();
      }, 300);
    } catch (error) {
      this.loggerService.logError(this.welcomeTourError(error),"ngAfterViewInit");
    }
  }

  /**
   * Angular lifecycle hook - cleans up Shepherd tour on component destroy.
   */
  ngOnDestroy(): void {
    try {
      if (this.tour) {
        // Remove event listeners to prevent any callbacks
        this.tour.off('complete');
        this.tour.off('cancel');
        this.tour.complete();
        this.tour = null;
      }
      
      // Reset the flag
      this.tourClosed = false;
    } catch (error) {
      this.loggerService.logError(this.welcomeTourError(error),"ngOnDestroy");
    }
  }

  /**
   * Initialize the Shepherd tour with configuration
   */
  private initializeTour(): void {
    try {
      // Use global Shepherd if available (for testing), otherwise use imported Shepherd
      const ShepherdConstructor = (window as any).Shepherd || Shepherd;
      this.tour = new ShepherdConstructor.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          classes: 'freya-tour-step',
          scrollTo: { behavior: 'smooth', block: 'center' },
          cancelIcon: {
            enabled: true
          }
        }
      });

      // Add welcome step
      this.addWelcomeStep();

      // Add all tour steps
      this.steps.slice(1).forEach((stepConfig, index) => {
        this.addTourStep(stepConfig, index + 1);
      });

      // Set up tour event handlers
      this.tour.on('complete', () => this.onTourComplete());
      this.tour.on('cancel', () => this.onTourSkip());

    } catch (error) {
      this.loggerService.logError(this.welcomeTourError(error), "initializeTour");
    }
  }

  /**
   * Start the Shepherd tour
   */
  private startTour(): void {
    try {
      if (this.tour) {
        this.tour.start();
      }
    } catch (error) {
      this.loggerService.logError(this.welcomeTourError(error), "startTour");
    }
  }

  /**
   * Add the welcome card step to the tour
   */
  private addWelcomeStep(): void {
    if (!this.tour) return;

    this.tour.addStep({
      title: `<div style="text-align: center; display: flex; justify-content: center; align-items: center;"><strong>${this.translate.instant('WELCOME_CARD_TITLE')}</strong> <span class="hand-emoji">👋</span></div>`,
      text: `<div class="welcome-content">${this.updatedWelcome}</div>`,
      classes: 'freya-welcome-step',
      buttons: [
        {
          text: this.translate.instant('SKIP_BUTTON_TEXT'),
          classes: 'skip-btn',
          action: () => this.skipTour()
        },
        {
          text: this.translate.instant('START_BUTTON_TEXT'),
          classes: 'start-btn',
          action: () => this.nextStep()
        }
      ],
      modalOverlayOpeningPadding: 0,
      modalOverlayOpeningRadius: 0
    });
  }

  /**
   * Add a tour step with element highlighting
   */
  private addTourStep(stepConfig: any, stepNumber: number): void {
    if (!this.tour) return;

    const element = document.querySelector(stepConfig.element);

    const totalSteps = this.steps.length - 1; // Exclude welcome step
    
    // Adjust padding and radius based on the target element
    const elementConfig = {
      '.chat-menu-item': { padding: 4 },
      '#new_topic_button': { padding: 4 },
      '.prompt-library': { padding: 6 },
      '.dropdown-selected': { padding: 2 },
      '.voice-input-container': { padding: 8 }
    };
    
    const config = elementConfig[stepConfig.element as keyof typeof elementConfig] || {};
    const overlayPadding = config.padding || 10;
    const overlayRadius = 12;
    
    const stepOptions: any = {
      title: `<strong>${this.translate.instant(stepConfig.title)}</strong>`,
      text: this.translate.instant(stepConfig.content),
      classes: 'freya-tour-step',
      buttons: this.createStepButtons(stepNumber, totalSteps),
      modalOverlayOpeningPadding: overlayPadding,
      modalOverlayOpeningRadius: overlayRadius,
      when: {
        show: () => {
          this.emitHighlightEvent(stepNumber);
          this.addPaginationToFooter(stepNumber, totalSteps);
        },
        hide: () => {
          this.clearHighlights();
        }
      }
    };

    // Only attach to element if it exists
    if (stepConfig.element && element) {
      stepOptions.attachTo = {
        element: stepConfig.element,
        on: stepConfig.position || 'bottom'
      };
    }

    this.tour.addStep(stepOptions);
  }

  /**
   * Create buttons for tour steps
   */
  private createStepButtons(stepNumber: number, totalSteps: number): Array<any> {
    const buttons = [];

    if (stepNumber < totalSteps) {
      buttons.push({
        text: this.translate.instant('SKIP_BUTTON_TEXT'),
        classes: 'skip-btn',
        action: () => this.skipTour()
      });
      buttons.push({
        text: this.translate.instant('NEXT_BUTTON_TEXT'),
        classes: 'next-btn',
        action: () => this.nextStep()
      });
    } else {
      buttons.push({
        text: this.translate.instant('END_BUTTON_TEXT'),
        classes: 'end-btn',
        action: () => this.closeTour()
      });
    }

    return buttons;
  }

  /**
   * Add pagination to the footer alongside buttons
   */
  private addPaginationToFooter(stepNumber: number, totalSteps: number): void {
    setTimeout(() => {
      const currentStep = this.tour?.getCurrentStep();
      const shepherdElement = currentStep?.el;
      
      if (!shepherdElement) return;

      const footer = shepherdElement.querySelector('.shepherd-footer');
      if (footer) {
        // Remove existing pagination if any
        const existingPagination = footer.querySelector('.shepherd-pagination');
        if (existingPagination) {
          existingPagination.remove();
        }

        // Add pagination to the left side of footer (before buttons)
        const pagination = document.createElement('span');
        pagination.className = 'shepherd-pagination';
        pagination.textContent = `${stepNumber}/${totalSteps}`;
        footer.insertBefore(pagination, footer.firstChild);
      }
    }, 50);
  }



  /**
   * Move to the next step in the tour
   */
  nextStep(): void {
    if (this.tour) {
      this.tour.next();
    }
  }

  /**
   * Skip the tour entirely
   */
  skipTour(): void {
    if (this.tour && !this.tourClosed) {
      // Close the tour (this will handle everything including API call)
      this.closeTour();
    }
  }

  /**
   * Emit highlight event for the current step
   */
  private emitHighlightEvent(stepNumber: number): void {
    this.step = stepNumber + 1; // Adjust for welcome step
    const highlightData = {
      step: this.step,
      highlightPromptSection: stepNumber === 1,
      highlightSend: stepNumber === 2,
      highlightMic: stepNumber === 3,
      highlightOptions: stepNumber === 4,
      highlightPromptLibrary: stepNumber === 5,
      highlightChatHistoryBtn: stepNumber === 6,
      highlightNewChatBtn: stepNumber === 7,
      highlightHelpIcon: stepNumber === 8,
    };
    this.highlight.emit(highlightData);
  }

  /**
   * Clear all highlights
   */
  private clearHighlights(): void {
    const highlightData = {
      step: 0,
      highlightSend: false,
      highlightMic: false,
      highlightOptions: false,
      highlightPromptLibrary: false,
      highlightChatHistoryBtn: false,
      highlightNewChatBtn: false,
      highlightHelpIcon: false,
      highlightPromptSection: false
    };
    this.highlight.emit(highlightData);
  }

  /**
   * Handle tour completion
   */
  private onTourComplete(): void {
    if (!this.tourClosed) {
      this.closeTour();
    }
  }

  /**
   * Handle tour skip
   */
  private onTourSkip(): void {
    if (!this.tourClosed) {
      this.closeTour();
    }
  }


  /**
   * Update tour status via API call asynchronously
   */
  private updateTourStatusAsync(): void {
    try {
      // This runs asynchronously and doesn't block the UI
      this.chatbotService.callUpdateWizardTour(
        environment.LoginUserID,
        this.tenantId,
        this.isIntroWizardEnabled,
        this.isTourEnabled,
        { status: "completed" },
        { status: this.introWizardStatus }
      );
    } catch (error) {
      this.loggerService.logError(this.welcomeTourError(error), "updateTourStatusAsync");
    }
  }

  /**
   * Closes the tour immediately and resets highlight states.
   * Emits close events to parent and handles API call asynchronously.
   */
  closeTour() {
    try {
      // Prevent duplicate calls
      if (this.tourClosed) {
        return;
      }
      this.tourClosed = true;
      
      // Complete the Shepherd tour if it exists (but avoid triggering events again)
      if (this.tour) {
        // Remove event listeners first to prevent recursive calls
        this.tour.off('complete');
        this.tour.off('cancel');
        this.tour.complete();
        this.tour = null;
      }

      // Clear all highlights immediately
      this.clearHighlights();
      
      // Emit close event immediately
      const tourStatus = "completed";
      this.emitTourVisible.emit(tourStatus);
      this.close.emit();

      // Handle API call asynchronously without blocking UI
      this.updateTourStatusAsync();
      
    } catch (error) {
      this.loggerService.logError(this.welcomeTourError(error), "closeTour");
    }
  }

  
}
